// src/utils/hardReset.js
import { db } from "@/firebase";
import { terminate, clearIndexedDbPersistence } from "firebase/firestore";

/**
 * Cierra listeners de Firestore usando la función registrada por AppCore
 */
function disconnectAllListeners() {
  try {
    if (typeof window.getDisconnectFirestore === "function") {
      const disconnect = window.getDisconnectFirestore();
      if (typeof disconnect === "function") {
        disconnect();
        console.log("[HARD_RESET] ✅ Listeners cerrados");
        return true;
      }
    }
  } catch (error) {
    // Silenciar AbortError esperado al cerrar streams
    if (error.name === 'AbortError') {
      console.log("[HARD_RESET] ⚠️  AbortError (esperado al cerrar streams)");
      return true;
    }
    console.warn("[HARD_RESET] ⚠️  Error cerrando listeners:", error.message);
  }
  return false;
}

/**
 * Termina la instancia de Firestore y limpia persistencia
 */
async function terminateFirestore() {
  try {
    console.log("[HARD_RESET] Terminando Firestore...");
    await terminate(db);
    console.log("[HARD_RESET] ✅ Firestore terminado");
    
    try {
      await clearIndexedDbPersistence(db);
      console.log("[HARD_RESET] ✅ Persistencia limpiada");
    } catch (error) {
      // clearIndexedDbPersistence puede fallar si hay conexiones activas
      console.log("[HARD_RESET] ⚠️  clearIndexedDbPersistence no disponible (esperado)");
    }
  } catch (error) {
    // Silenciar AbortError
    if (error.name === 'AbortError') {
      console.log("[HARD_RESET] ⚠️  AbortError en terminate (esperado)");
      return;
    }
    console.warn("[HARD_RESET] ⚠️  Error terminando Firestore:", error.message);
  }
}

/**
 * Elimina bases de datos de IndexedDB con manejo de promesas
 */
function deleteIndexedDB(dbName) {
  return new Promise((resolve) => {
    try {
      const request = indexedDB.deleteDatabase(dbName);
      
      request.onsuccess = () => {
        console.log(`[HARD_RESET] ✅ IndexedDB "${dbName}" eliminada`);
        resolve(true);
      };
      
      request.onerror = () => {
        console.log(`[HARD_RESET] ⚠️  Error eliminando "${dbName}"`);
        resolve(false);
      };
      
      request.onblocked = () => {
        console.log(`[HARD_RESET] ⚠️  "${dbName}" bloqueada, se limpiará al recargar`);
        resolve(false);
      };
      
      // Timeout para evitar colgarse
      setTimeout(() => {
        console.log(`[HARD_RESET] ⏰ Timeout eliminando "${dbName}"`);
        resolve(false);
      }, 5000);
      
    } catch (error) {
      console.log(`[HARD_RESET] ⚠️  "${dbName}" no accesible:`, error.message);
      resolve(false);
    }
  });
}

/**
 * Limpia todas las bases de datos de IndexedDB
 */
async function cleanAllIndexedDB() {
  console.log("[HARD_RESET] Limpiando IndexedDB...");
  
  const databases = [
    'firebaseLocalStorageDb',
    'firestore/[DEFAULT]/camperros-rutas/main'
  ];
  
  const results = await Promise.all(databases.map(db => deleteIndexedDB(db)));
  const successful = results.filter(Boolean).length;
  
  console.log(`[HARD_RESET] ✅ IndexedDB limpiada (${successful}/${databases.length} exitosas)`);
}

/**
 * Limpia localStorage y sessionStorage
 */
function cleanStorage() {
  console.log("[HARD_RESET] Limpiando storage...");
  
  let localRemoved = 0;
  let sessionRemoved = 0;
  
  // Limpiar localStorage (excepto auth de Firebase)
  Object.keys(localStorage).forEach(key => {
    if (!key.startsWith('firebase:')) {
      localStorage.removeItem(key);
      localRemoved++;
    }
  });
  
  // Limpiar sessionStorage completo
  sessionRemoved = sessionStorage.length;
  sessionStorage.clear();
  
  console.log(`[HARD_RESET] ✅ Storage limpiado (localStorage: ${localRemoved}, sessionStorage: ${sessionRemoved})`);
}

/**
 * Reset completo y limpio sin errores molestos
 * 
 * Esta función:
 * 1. Cierra todos los listeners guardados
 * 2. Termina Firestore con terminate() y clearIndexedDbPersistence()
 * 3. Limpia IndexedDB con manejo correcto de promesas y onblocked
 * 4. Limpia localStorage y sessionStorage
 * 5. Recarga la página
 * 6. Captura y silencia AbortError (esperado al cerrar streams)
 */
export async function hardReset() {
  console.log("\n🔄 HARD RESET INICIADO\n");
  
  try {
    // Paso 1: Cerrar listeners
    console.log("[HARD_RESET] Paso 1/5: Cerrando listeners...");
    disconnectAllListeners();
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Paso 2: Terminar Firestore
    console.log("[HARD_RESET] Paso 2/5: Terminando Firestore...");
    await terminateFirestore();
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Paso 3: Limpiar IndexedDB
    console.log("[HARD_RESET] Paso 3/5: Limpiando IndexedDB...");
    await cleanAllIndexedDB();
    
    // Paso 4: Limpiar storage
    console.log("[HARD_RESET] Paso 4/5: Limpiando storage...");
    cleanStorage();
    
    // Paso 5: Recargar página
    console.log("[HARD_RESET] Paso 5/5: Recargando página...");
    console.log("\n✅ HARD RESET COMPLETADO\n");
    console.log("⏳ Recargando página en 1 segundo...\n");
    
    setTimeout(() => {
      location.reload();
    }, 1000);
    
  } catch (error) {
    // Silenciar AbortError
    if (error.name === 'AbortError') {
      console.log("[HARD_RESET] ⚠️  AbortError capturado y silenciado (esperado)");
      console.log("⏳ Recargando página...");
      setTimeout(() => location.reload(), 500);
      return;
    }
    
    // Otros errores
    console.error("[HARD_RESET] ❌ Error durante reset:", error);
    console.log("⏳ Recargando de todas formas para completar limpieza...");
    setTimeout(() => location.reload(), 1000);
  }
}

// Función de conveniencia para usar desde la consola del navegador
if (typeof window !== 'undefined') {
  window.hardReset = hardReset;
  console.log("💡 Tip: Puedes ejecutar hardReset() desde la consola del navegador");
}
